import { useQuery } from "@apollo/client";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useState } from "react";

import { completedCorrections, CorrectionDraft, EMPTY_CORRECTION_DRAFT } from "./correctionDraft";
import { CreateManifestCorrectionFields } from "./CreateManifestCorrectionFields";
import { ExceedanceKey } from "./exceedanceKeys";
import { isCorrectionLanded, ManifestCorrectionRow } from "./manifestCorrectionDisplay";
import { ManifestCorrectionPicker } from "./ManifestCorrectionPicker";
import { LinkableSubmissionOption, SubmissionPicker } from "./SubmissionPicker";
import { useResolutionMutations } from "./useResolutionMutations";

const LINKABLE_SUBMISSIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableSubmissions($facilityId: ID!, $search: String) {
    generatorStatusExceedanceLinkableSubmissions(facilityId: $facilityId, search: $search) {
      id
      title
      submissionType
      status
      dueDate
      createdAt
      facilityName
      assigneeName
    }
  }
`);

export const LINKABLE_MANIFEST_CORRECTIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableManifestCorrections(
    $facilityId: ID!
    $source: String!
    $periodStart: Date!
    $search: String
  ) {
    generatorStatusExceedanceLinkableManifestCorrections(
      facilityId: $facilityId
      source: $source
      periodStart: $periodStart
      search: $search
    ) {
      id
      processedManifestId
      manifestTrackingNumber
      shippedDate
      wasteLineNumber
      field
      staffReported
      status
      resolutionPath
      eManifestValue
      vendorValue
      correctValue
      resolvedAt
      resolvedByName
    }
  }
`);

/** Which resolution the dialog is about to record. */
type ResolveMode = "acknowledge" | "link" | "correction";

const SAVE_TEXT: Record<ResolveMode, string> = {
  acknowledge: "Mark resolved",
  link: "Link submission",
  correction: "Resolve via correction",
};

const SUCCESS_MESSAGE: Record<ResolveMode, string> = {
  acknowledge: "Marked resolved.",
  link: "Linked to submission.",
  correction: "Resolved via manifest correction.",
};

const CORRECTION_PARK_SAVE_TEXT = "Link corrections";
const CORRECTION_PARK_SUCCESS = "Waiting on the linked manifest corrections.";
const CREATE_CORRECTION_SAVE_TEXT = "Create and link corrections";

/** Under "Manifest correction": cite corrections already on file, or create one that isn't. */
type CorrectionAction = "cite" | "create";

/** Save button text: says whether saving resolves the finding or parks it. */
function saveTextFor(
  mode: ResolveMode,
  correctionAction: CorrectionAction,
  parksOnCorrections: boolean
): string {
  if (mode !== "correction") return SAVE_TEXT[mode];
  if (correctionAction === "create") return CREATE_CORRECTION_SAVE_TEXT;
  return parksOnCorrections ? CORRECTION_PARK_SAVE_TEXT : SAVE_TEXT.correction;
}

function toResolveMode(value: string): ResolveMode {
  if (value === "link") return "link";
  if (value === "correction") return "correction";
  return "acknowledge";
}

type MarkResolvedDialogProps = {
  open: boolean;
  onClose: () => void;
  exceedanceKey: ExceedanceKey;
  facilityId: string;
  /** Builds the per-candidate link-outs to submissions and manifests. */
  tenantId: string;
  /** Preselects the corrections already linked to this row. */
  citedCorrectionIds?: string[];
  onResolved: () => void;
  /** The new correction appears in the drawer, so the caller can keep it open. */
  onCorrectionCreated: () => void;
};

export function MarkResolvedDialog({
  open,
  onClose,
  exceedanceKey,
  facilityId,
  tenantId,
  citedCorrectionIds,
  onResolved,
  onCorrectionCreated,
}: MarkResolvedDialogProps) {
  const [mode, setMode] = useState<ResolveMode>("acknowledge");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<LinkableSubmissionOption | null>(null);
  const [selectedCorrectionIds, setSelectedCorrectionIds] = useState<string[]>([]);
  const [correctionAction, setCorrectionAction] = useState<CorrectionAction>("cite");
  const [draft, setDraft] = useState<CorrectionDraft>(EMPTY_CORRECTION_DRAFT);
  // Server-side search: each picker returns only the newest candidates, so older
  // ones are reachable by narrowing rather than paging.
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const { data, loading: loadingOptions } = useQuery(LINKABLE_SUBMISSIONS_QUERY, {
    variables: { facilityId, search: trimmedSearch || undefined },
    skip: !open || mode !== "link",
    fetchPolicy: "cache-and-network",
  });

  const { data: correctionData, loading: loadingCorrections } = useQuery(
    LINKABLE_MANIFEST_CORRECTIONS_QUERY,
    {
      variables: {
        facilityId,
        source: exceedanceKey.source,
        periodStart: exceedanceKey.periodStart,
        search: trimmedSearch || undefined,
      },
      skip: !open || mode !== "correction",
      fetchPolicy: "cache-and-network",
    }
  );

  const { resolve, create, submitting } = useResolutionMutations({
    onResolved: () => {
      reset();
      onResolved();
    },
    onCreated: () => {
      reset();
      onCorrectionCreated();
    },
  });

  const options: LinkableSubmissionOption[] =
    data?.generatorStatusExceedanceLinkableSubmissions ?? [];
  const corrections: ManifestCorrectionRow[] =
    correctionData?.generatorStatusExceedanceLinkableManifestCorrections ?? [];

  // Initialize on open, after the drawer loads citations; preserve edits while open.
  useEffect(() => {
    if (open) setSelectedCorrectionIds(citedCorrectionIds ?? []);
    // oxlint-disable-next-line exhaustive-deps
  }, [open]);

  // A search that filters the selected row out of the grid also clears the
  // selection — an invisible selection would otherwise still be submittable.
  useEffect(() => {
    if (selected && data && !options.some((o) => o.id === selected.id)) {
      setSelected(null);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [data]);

  // Derived, not stored, for the same reason — filtering at render keeps a pick
  // alive if the search that hid it is widened again.
  const visibleCorrections = corrections.filter((c) => selectedCorrectionIds.includes(c.id));
  const visibleCorrectionIds = visibleCorrections.map((c) => c.id);
  // Warn about omitted selections once the search finishes loading.
  const hiddenSelectedCount = loadingCorrections
    ? 0
    : selectedCorrectionIds.length - visibleCorrectionIds.length;
  // ALL, not ANY: each checked fix accounts for part of the excess, so one landing
  // is no reason to clear the row.
  const parksOnCorrections =
    visibleCorrections.length > 0 && !visibleCorrections.every(isCorrectionLanded);
  const creatingCorrection = mode === "correction" && correctionAction === "create";
  const newCorrections = completedCorrections(draft);

  // Re-seeded rather than cleared: the dialog is mounted alongside the drawer, so a
  // second open must show the row's CURRENT citations, not the last edit's.
  const reset = () => {
    setMode("acknowledge");
    setNote("");
    setSelected(null);
    setSelectedCorrectionIds(citedCorrectionIds ?? []);
    setCorrectionAction("cite");
    setDraft(EMPTY_CORRECTION_DRAFT);
    setSearch("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleModeChange = (value: string) => {
    // Each mode searches a different catalog, so a carried-over search string
    // would silently filter the new picker to nothing.
    setSearch("");
    setMode(toResolveMode(value));
  };

  const handleSubmit = async () => {
    if (creatingCorrection) {
      if (!newCorrections || !draft.manifest) return;
      await create({
        facilityId: exceedanceKey.facilityId,
        source: exceedanceKey.source,
        periodStart: exceedanceKey.periodStart,
        processedManifestId: draft.manifest.id,
        corrections: newCorrections.map(({ field, correctValue, wasteLineUnifiedId }) => ({
          field,
          correctValue,
          wasteLineUnifiedId,
        })),
        note: note.trim(),
      });
      return;
    }
    await resolve(
      {
        exceedances: [exceedanceKey],
        note: note.trim(),
        submissionId: mode === "link" ? selected?.id : undefined,
        manifestDiscrepancyIds: mode === "correction" ? visibleCorrectionIds : undefined,
      },
      mode === "correction" && parksOnCorrections ? CORRECTION_PARK_SUCCESS : SUCCESS_MESSAGE[mode]
    );
  };

  // A note is required: it's the audit record of why/how this was resolved.
  const nothingCited =
    (mode === "link" && !selected) || (mode === "correction" && visibleCorrectionIds.length === 0);
  const nothingPicked = creatingCorrection ? !newCorrections : nothingCited;
  const submitDisabled = submitting || nothingPicked || !note.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 820, maxWidth: "90vw" } } }}
      // Top-anchored so switching resolution types only moves the bottom edge.
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      title="Mark resolved"
      description="Record a resolution or link work that is still in progress."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: submitting,
        loadingText: "Saving...",
        hideStartIcon: true,
        saveText: saveTextFor(mode, correctionAction, parksOnCorrections),
        disabled: submitDisabled,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      <ResolutionModeSelect mode={mode} onChange={handleModeChange} />

      {mode === "link" && (
        <SubmissionPicker
          rows={options}
          loading={loadingOptions}
          search={search}
          onSearchChange={setSearch}
          searching={trimmedSearch.length > 0}
          selectedId={selected?.id ?? null}
          onSelect={(id) => setSelected(options.find((o) => o.id === id) ?? null)}
          tenantId={tenantId}
        />
      )}

      {mode === "correction" && (
        <Box sx={{ mt: 1 }}>
          <CorrectionActionToggle
            value={correctionAction}
            onChange={(value) => {
              setSearch("");
              setCorrectionAction(value);
            }}
          />
          {correctionAction === "cite" ? (
            <ManifestCorrectionPicker
              rows={corrections}
              loading={loadingCorrections}
              search={search}
              onSearchChange={setSearch}
              searching={trimmedSearch.length > 0}
              selectedIds={visibleCorrectionIds}
              hiddenSelectedCount={hiddenSelectedCount}
              onSelectionChange={setSelectedCorrectionIds}
              tenantId={tenantId}
            />
          ) : (
            <CreateManifestCorrectionFields
              exceedanceKey={exceedanceKey}
              tenantId={tenantId}
              draft={draft}
              onDraftChange={setDraft}
            />
          )}
        </Box>
      )}

      <TextField
        label="Note (required)"
        required
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Dialog>
  );
}

function CorrectionActionToggle({
  value,
  onChange,
}: {
  value: CorrectionAction;
  onChange: (value: CorrectionAction) => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_event, next: CorrectionAction | null) => {
        if (next) onChange(next);
      }}
      sx={{ mb: 1 }}
    >
      <ToggleButton value="cite">Link existing</ToggleButton>
      <ToggleButton value="create">Create new</ToggleButton>
    </ToggleButtonGroup>
  );
}

function ResolutionModeSelect({
  mode,
  onChange,
}: {
  mode: ResolveMode;
  onChange: (value: string) => void;
}) {
  return (
    <RadioGroup value={mode} onChange={(e) => onChange(e.target.value)}>
      <FormControlLabel
        value="acknowledge"
        control={<Radio size="small" />}
        label="Corrected elsewhere — no submission needed"
      />
      <FormControlLabel
        value="link"
        control={<Radio size="small" />}
        label="Link to an existing submission"
      />
      <FormControlLabel
        value="correction"
        control={<Radio size="small" />}
        label="Manifest correction"
      />
    </RadioGroup>
  );
}
